import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { getGenericErrorMessage } from '../shared/utils/errors';
import { clearLazyRetryReloadFlag } from '../shared/utils/lazyWithRetry';
import { reportClientCrash } from '../shared/observability/crashReporter';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  /**
   * The server-side record this crash was written to, once the report lands.
   * `null` until then and forever after if it never does, which is what the
   * message's id-less arm renders.
   */
  errorId: string | null;
}

/**
 * Production app-level error boundary.
 *
 * Catches render-time crashes in the React tree (the class of failure the global
 * mutation/query error toasts cannot reach — those only handle rejected API
 * promises, not a component throwing during render) and shows PanLuma's standard
 * unexpected-error message: a friendly, translated line that tells the user to
 * report it via the support widget, with a reference.
 *
 * It also REPORTS the crash. Until WI 2e200bde a render crash was recorded
 * nowhere at all — `componentDidCatch` called `console.error` and stopped, so the
 * message, stack and component stack reached the user's own devtools and no
 * further. The reference on screen was a bare timestamp that identified nothing,
 * and establishing what had actually broken meant correlating that timestamp
 * against a deploy log by hand (WI ad05f876). The report now creates a durable
 * server-side record and the message quotes ITS id, which is the reference
 * `AGENTS.md` § Errors already requires of every other system error.
 *
 * The developer-facing `ErrorBoundaryDebug` (red box + component stack) is a
 * separate, dev-only tool — this one is what production users see.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  /**
   * Guards the post-report `setState`. The report is awaited, and by the time it
   * resolves this boundary may have been unmounted — by a reload, or by the user
   * navigating out of a subtree the inner boundary wraps.
   */
  private mounted = false;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorId: null };
  }

  static getDerivedStateFromError(): Pick<State, 'hasError'> {
    return { hasError: true };
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Diagnostics for the browser console. Kept alongside the server report, not
    // replaced by it: a developer with devtools open wants the unredacted object.
    console.error('[ErrorBoundary] Unhandled render error:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // `reportClientCrash` never rejects and never throws — see its module
    // docstring for why every branch of it is inside a try/catch. The trailing
    // `.catch` is the belt to that braces, and it is not decoration: a rejection
    // escaping THIS handler is an unhandled rejection raised from inside React's
    // error path, and a throw would take the fallback screen down with it and
    // leave the user a blank page instead of a message.
    void reportClientCrash({
      error,
      componentStack: errorInfo.componentStack,
    })
      .then((errorId) => {
        if (errorId && this.mounted) this.setState({ errorId });
      })
      .catch(() => {
        /* Reporting failed. The screen keeps its timestamp-only reference. */
      });
  }

  render() {
    if (this.state.hasError) {
      return <AppErrorFallback errorId={this.state.errorId} />;
    }
    return this.props.children;
  }
}

/**
 * A user pressing Reload is asking us to try again, so give back the one-shot
 * recovery reload a failed route-chunk import may already have spent. Without
 * this the button cannot recover from the commonest cause of landing here — a
 * chunk that failed during a deploy — because `lazyWithRetry` re-throws on
 * sight of its own spent flag (WI ad05f876).
 */
function handleReload() {
  clearLazyRetryReloadFlag();
  window.location.reload();
}

function AppErrorFallback({ errorId }: { errorId: string | null }) {
  const { t } = useTranslation();

  return (
    <div
      data-testid="app-error-boundary"
      className="flex min-h-screen w-full flex-col items-center justify-center gap-4 bg-background p-6 text-center"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
        <AlertTriangle className="h-6 w-6 text-destructive" />
      </div>
      <h1 className="text-lg font-semibold text-foreground">
        {t('common:errorBoundary.title')}
      </h1>
      <p data-testid="app-error-boundary-message" className="max-w-md text-sm text-muted-foreground">
        {/* `errorId` is null until the report lands and stays null if it never
            does; `getGenericErrorMessage` then takes the id-less arm rather than
            rendering "Reference:  at <time>"
            (tools/check_error_reference_idless_variant.py). */}
        {getGenericErrorMessage(undefined, { errorId })}
      </p>
      <Button onClick={handleReload} className="mt-2">
        {t('common:errorBoundary.reload')}
      </Button>
    </div>
  );
}
